import { Button } from "@package/subframe/components/Button";
import { Table } from "@package/subframe/components/Table";
import { Fragment } from "react";

import { AcknowledgementStateBadges } from "@/components/acknowledgements/AcknowledgementStateBadges";
import {
  TABLE_MIN_WIDTH,
  TableScrollContainer,
} from "@/components/ui/table-scroll-container";
import type { NurseryRole } from "@/contexts/NurseryContext";
import type { AcknowledgementRosterRowDto } from "@/generated/model";
import type { RosterDateCell } from "@/lib/acknowledgements/tracking";
import {
  canRemind,
  REMINDER_ACTIONS_ENABLED,
  rosterDateCells,
} from "@/lib/acknowledgements/tracking";
import { nurseryRoleLabel } from "@/lib/roles";

interface AcknowledgementTrackingTableProps {
  roster: AcknowledgementRosterRowDto[];
  canSendReminders: boolean;
  remindingRequestId: string | null;
  onRemind: (requestId: string) => void;
}

/**
 * Who has and has not acknowledged, with the due, read, signed and reminder
 * dates (AC24).
 *
 * Read and Signed are shown as a PAIR rather than one status, because AC15
 * requires both and a manager chasing signatures needs to know the difference
 * between "hasn't looked" and "looked and didn't commit".
 */
export function AcknowledgementTrackingTable({
  roster,
  canSendReminders,
  remindingRequestId,
  onRemind,
}: AcknowledgementTrackingTableProps) {
  const now = new Date();

  return (
    <TableScrollContainer>
      <Table
        // Eight columns, nine with the Actions column.
        className={
          REMINDER_ACTIONS_ENABLED ? TABLE_MIN_WIDTH.xl : TABLE_MIN_WIDTH.lg
        }
        data-testid="ack-tracking-table"
        header={
          <Table.HeaderRow>
            <Table.HeaderCell>Staff member</Table.HeaderCell>
            <Table.HeaderCell>Role</Table.HeaderCell>
            <Table.HeaderCell>Status</Table.HeaderCell>
            <Table.HeaderCell>Due by</Table.HeaderCell>
            <Table.HeaderCell>Read on</Table.HeaderCell>
            <Table.HeaderCell>Signed on</Table.HeaderCell>
            <Table.HeaderCell>Last reminded</Table.HeaderCell>
            <Table.HeaderCell>Next reminder due</Table.HeaderCell>
            {REMINDER_ACTIONS_ENABLED && (
              <Table.HeaderCell variant="aligned-right">
                Actions
              </Table.HeaderCell>
            )}
          </Table.HeaderRow>
        }
      >
        {roster.map((row) => {
          const dates = rosterDateCells(row, now);
          return (
            <Table.Row key={row.requestId}>
              {/* Double share: an email truncated to a sixth of the table is
                unreadable. Capped all the same, so one long email truncates
                instead of pushing the dates off screen. */}
              <Table.Cell className="h-14 grow-[2] shrink-0 basis-0">
                <div className="flex min-w-0 max-w-80 flex-col">
                  <span
                    className="text-body-bold font-body-bold text-neutral-900 truncate"
                    title={row.staffName ?? row.staffEmail}
                  >
                    {row.staffName ?? row.staffEmail}
                  </span>
                  {row.staffName && (
                    <span
                      className="text-caption font-caption text-subtext-color truncate"
                      title={row.staffEmail}
                    >
                      {row.staffEmail}
                    </span>
                  )}
                </div>
              </Table.Cell>
              <Table.Cell className="h-14 grow shrink-0 basis-0">
                <span className="text-body font-body text-subtext-color">
                  {row.role
                    ? nurseryRoleLabel(row.role as NurseryRole)
                    : "No longer a member"}
                </span>
              </Table.Cell>
              <Table.Cell className="h-14 grow shrink-0 basis-0">
                <AcknowledgementStateBadges
                  requestId={row.requestId}
                  readAt={row.readAt}
                  signedAt={row.signedAt}
                />
              </Table.Cell>
              <Table.Cell className="h-14 grow shrink-0 basis-0">
                {dates.due.overdue ? (
                  <div className="flex flex-col">
                    <DateText
                      text={dates.due.text}
                      className="text-error-700"
                    />
                    <span className="text-caption font-caption text-error-700">
                      {dates.due.overdueText}
                    </span>
                  </div>
                ) : (
                  <DateCellText cell={dates.due} />
                )}
              </Table.Cell>
              <Table.Cell className="h-14 grow shrink-0 basis-0">
                <DateCellText cell={dates.read} />
              </Table.Cell>
              <Table.Cell className="h-14 grow shrink-0 basis-0">
                <DateCellText cell={dates.signed} />
              </Table.Cell>
              <Table.Cell className="h-14 grow shrink-0 basis-0">
                <DateCellText cell={dates.lastReminded} />
              </Table.Cell>
              <Table.Cell className="h-14 grow shrink-0 basis-0">
                <DateCellText cell={dates.nextReminder} />
              </Table.Cell>
              {REMINDER_ACTIONS_ENABLED && (
                <Table.Cell
                  variant="aligned-right"
                  className="h-14 grow shrink-0 basis-0"
                >
                  {canSendReminders && canRemind(row, now) && (
                    <Button
                      variant="neutral-secondary"
                      size="small"
                      loading={remindingRequestId === row.requestId}
                      onClick={() => onRemind(row.requestId)}
                      data-testid={`ack-remind-${row.requestId}`}
                    >
                      Remind
                    </Button>
                  )}
                </Table.Cell>
              )}
            </Table.Row>
          );
        })}
      </Table>
    </TableScrollContainer>
  );
}

function DateCellText({ cell }: { cell: RosterDateCell }) {
  if (cell.empty) {
    return (
      <span className="text-body font-body text-subtext-color">
        {cell.text}
      </span>
    );
  }
  return <DateText text={cell.text} className="text-default-font" />;
}

/**
 * Wraps only between the date and the time. Left alone, the browser breaks
 * after a hyphen and leaves "2026-" on one line and "09-22" on the next.
 */
function DateText({ text, className }: { text: string; className: string }) {
  return (
    <span className={`text-body font-body ${className}`}>
      {text.split(" ").map((part, index) => (
        <Fragment key={part}>
          {index > 0 && " "}
          <span className="whitespace-nowrap">{part}</span>
        </Fragment>
      ))}
    </span>
  );
}
